
import React, { useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { updateMetaTags, getCanonicalUrl } from '@/utils/seoUtils';
import { generateEnhancedSchema, generateBreadcrumbData } from '@/utils/accessibleSEO';
import { useLocation } from 'react-router-dom';

const LegalHeader: React.FC = () => {
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];
  const location = useLocation();
  
  useEffect(() => {
    // Update SEO meta tags based on current language with improved keywords from the research
    const metaTitles = {
      de: `${t.legalTitle} | Future Level - KI-Videomarketing Agentur Schweiz`,
      en: `${t.legalTitle} | Future Level - AI Video Marketing Agency Switzerland`,
      fr: `${t.legalTitle} | Future Level - Agence de Marketing Vidéo IA Suisse`,
      it: `${t.legalTitle} | Future Level - Agenzia di Marketing Video IA Svizzera`
    };
    
    // Improved descriptions with keywords from the research
    const metaDescriptions = {
      de: `${t.legalTitle} - Future Level - KI-generierte Marketingvideos für Schweizer Unternehmen - ${t.legalDescription}`,
      en: `${t.legalTitle} - Future Level - AI-generated marketing videos for Swiss businesses - ${t.legalDescription}`,
      fr: `${t.legalTitle} - Future Level - Vidéos marketing générées par IA pour entreprises suisses - ${t.legalDescription}`,
      it: `${t.legalTitle} - Future Level - Video marketing generati da IA per aziende svizzere - ${t.legalDescription}`
    };
    
    // Language-specific keywords with enhanced research-based keywords and comparison terms
    const keywordsByLang = {
      de: "KI Videomarketing, KI-Videoproduktion, Future Level Impressum, KI Marketing Schweiz, KI-Videoerstellung, Videoagentur Emmen, KI-generierte Marketingvideos, KI vs traditionelle Videoproduktion, KI-Videomarketing Finanzsektor, KI-Videomarketing Luxusmarken",
      en: "AI video marketing, AI video production, Future Level legal notice, AI marketing Switzerland, AI video creation, Video agency Emmen, AI-generated marketing videos, AI vs traditional video production, AI video marketing finance sector, AI video marketing luxury brands",
      fr: "Marketing vidéo IA, Production vidéo IA, Mentions légales Future Level, IA marketing Suisse, Création vidéo IA, Agence vidéo Emmen, Vidéos marketing générées par IA, IA vs production vidéo traditionnelle, Marketing vidéo IA secteur financier, Marketing vidéo IA marques de luxe",
      it: "Marketing video IA, Produzione video IA, Note legali Future Level, IA marketing Svizzera, Creazione video IA, Agenzia video Emmen, Video marketing generati da IA, IA vs produzione video tradizionale, Marketing video IA settore finanziario, Marketing video IA marchi di lusso"
    };
    
    const pagePath = currentLanguage === 'de' ? '/impressum' : 
                    currentLanguage === 'en' ? '/legal' : 
                    currentLanguage === 'fr' ? '/mentions-legales' : '/note-legali';
    
    const canonicalUrl = getCanonicalUrl(pagePath.substring(1), currentLanguage);
    
    // Update meta tags with enhanced keywords from research
    updateMetaTags({
      title: metaTitles[currentLanguage as keyof typeof metaTitles],
      description: metaDescriptions[currentLanguage as keyof typeof metaDescriptions],
      keywords: keywordsByLang[currentLanguage as keyof typeof keywordsByLang],
      canonicalUrl: canonicalUrl,
      language: currentLanguage,
      ogType: "website"
    });
    
    // Generate schema markup for legal page with enhanced breadcrumbs
    const breadcrumbItems = generateBreadcrumbData(
      pagePath,
      t.legalTitle,
      currentLanguage
    );
    
    // Enhanced organization schema with more structured data and owner info
    generateEnhancedSchema({
      title: metaTitles[currentLanguage as keyof typeof metaTitles],
      description: metaDescriptions[currentLanguage as keyof typeof metaDescriptions],
      type: 'Organization',
      canonicalUrl: canonicalUrl,
      language: currentLanguage,
      keywords: keywordsByLang[currentLanguage as keyof typeof keywordsByLang],
      breadcrumbItems: breadcrumbItems,
      author: "Future Level"
    });
    
    // Add structured data with business information for local SEO
    const localBusinessSchema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Future Level",
      "image": "https://future-level.ch/logo.png",
      "url": canonicalUrl,
      "telephone": "+41 41 539 16 13",
      "email": "info@future-level.ch",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Zumhofstrasse",
        "addressLocality": "Kriens",
        "postalCode": "6010",
        "addressCountry": "CH"
      },
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "09:00",
          "closes": "18:00"
        }
      ],
      "priceRange": "$$",
      "makesOffer": [
        {
          "@type": "Offer",
          "name": "KI-generierte Produktvideos",
          "description": "Professionelle KI-generierte Videos für Produkte und Dienstleistungen"
        },
        {
          "@type": "Offer",
          "name": "KI-Social-Media-Werbung",
          "description": "Zielgerichtete KI-generierte Videos für Social-Media-Kampagnen"
        }
      ]
    };
    
    // Add the local business schema to the page
    let scriptTag = document.getElementById('local-business-schema') as HTMLScriptElement | null;
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.id = 'local-business-schema';
      scriptTag.setAttribute('type', 'application/ld+json');
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(localBusinessSchema);
    
  }, [currentLanguage, t, location.pathname]);

  return null;
};

export default LegalHeader;
